/**
 * Dependency Graph Utility Functions
 *
 * Shared utilities for working with dependency graph data across components.
 */

import { DependencyGraphData, WordNode } from '../components/dependency-graph/types';

/**
 * API response format (snake_case from backend)
 */
export interface ApiDependencyWordNode {
  id: string;
  word: string;
  role: string;
  role_native: string;
  color: string;
  // New fields from Gemini + Claude review approach
  lemma?: string;
  form?: string;
  definition_native?: string;
}

export interface ApiDependencyArc {
  from: string;
  to: string;
  label: string;
  label_en?: string;
}

export interface ApiDependencyAnimationStep {
  node_ids: string[];
  explanation: string;
  duration?: number;
}

export interface ApiDependencyGraphData {
  words: ApiDependencyWordNode[];
  arcs: ApiDependencyArc[];
  animation_steps: ApiDependencyAnimationStep[];
}

/**
 * Transform API dependency graph data (snake_case) to component format (camelCase)
 */
export const transformApiDependencyGraph = (apiData: ApiDependencyGraphData): DependencyGraphData => ({
  sentence: '', // Not needed for component rendering
  words: apiData.words.map(w => ({
    id: w.id,
    word: w.word,
    role: w.role,
    roleZh: w.role_native,
    color: w.color,
    // New fields from Gemini + Claude review approach
    lemma: w.lemma,
    form: w.form,
    definitionZh: w.definition_native,
  })),
  arcs: apiData.arcs.map(a => ({
    from: a.from,
    to: a.to,
    label: a.label,
    labelEn: a.label_en,
  })),
  animationSteps: apiData.animation_steps.map(s => ({
    nodeIds: s.node_ids,
    explanation: s.explanation,
    duration: s.duration,
  })),
});

/**
 * Word index mapping result
 */
export interface WordIndexMapping {
  originalWordIndex: number;      // Index in original sentence (before hyphen expansion)
  originalWord: string;           // The original word (possibly hyphenated)
  graphNodeId: string | null;     // The matching dependency graph node ID
  graphNode: WordNode | null;     // The matching node
}

/**
 * Find the dependency graph node for a given expanded word index.
 *
 * DictationPage expands hyphenated words like "thirty-two" into ["thirty-", "two"]
 * for individual input boxes. This function maps an expanded word index back to
 * the original sentence position and finds the corresponding dependency graph node.
 *
 * @param expandedWordIndex - Index in the expanded word array (DictationPage's correctWords)
 * @param expandedWords - The expanded words array from DictationPage
 * @param dependencyGraph - The dependency graph data for the sentence
 * @returns The mapping result with original index and graph node
 */
export const findWordInDependencyGraph = (
  expandedWordIndex: number,
  expandedWords: string[],
  dependencyGraph: DependencyGraphData
): WordIndexMapping => {
  // Step 1: Map expanded index back to original word index
  // Count how many original words we've seen by tracking hyphen parts
  let originalWordIndex = 0;
  let expandedIndex = 0;

  while (expandedIndex < expandedWordIndex && expandedIndex < expandedWords.length) {
    const word = expandedWords[expandedIndex];
    // If this word ends with hyphen, it's part of a hyphenated word
    if (word.endsWith('-')) {
      // Skip all parts until we find one that doesn't end with hyphen
      while (expandedIndex < expandedWords.length && expandedWords[expandedIndex].endsWith('-')) {
        expandedIndex++;
      }
      // Include the last part (without hyphen)
      if (expandedIndex < expandedWordIndex) {
        expandedIndex++;
        originalWordIndex++;
      }
    } else {
      expandedIndex++;
      if (expandedIndex <= expandedWordIndex) {
        originalWordIndex++;
      }
    }
  }

  // Handle the current word - check if it's part of a hyphenated group
  let startOfGroup = expandedWordIndex;
  let originalWord = expandedWords[expandedWordIndex] || '';

  // Look backwards to find start of hyphenated group
  while (startOfGroup > 0 && expandedWords[startOfGroup - 1]?.endsWith('-')) {
    startOfGroup--;
  }

  // Reconstruct original word if this is part of a hyphenated group
  if (startOfGroup < expandedWordIndex || expandedWords[expandedWordIndex]?.endsWith('-')) {
    let endOfGroup = expandedWordIndex;
    // Find end of group
    while (endOfGroup < expandedWords.length - 1 && expandedWords[endOfGroup].endsWith('-')) {
      endOfGroup++;
    }
    // Reconstruct the full word
    originalWord = expandedWords.slice(startOfGroup, endOfGroup + 1).join('');
  }

  // Recalculate original index based on start of group
  // Use a while loop to avoid issues with modifying the loop variable in nested loops
  originalWordIndex = 0;
  let i = 0;
  while (i < startOfGroup) {
    if (!expandedWords[i].endsWith('-')) {
      originalWordIndex++;
      i++;
    } else {
      // Skip all hyphen parts
      while (i < startOfGroup && expandedWords[i].endsWith('-')) {
        i++;
      }
      // Count the non-hyphen end part if exists within our range
      if (i < startOfGroup) {
        originalWordIndex++;
        i++;
      }
    }
  }

  // Step 2: Find the corresponding node in dependency graph
  // Note: The dependency graph may include punctuation as separate nodes,
  // but DictationPage's correctWords merges punctuation with adjacent words.
  // So we can't rely on position alone - must verify word text matches.
  const graphWords = dependencyGraph.words;
  const normalizedOriginal = normalizeForComparison(originalWord);

  let graphNode: WordNode | null = null;
  let graphNodeId: string | null = null;

  // Strategy 1: Try to find by word text (most reliable when punctuation nodes exist)
  for (const node of graphWords) {
    if (normalizeForComparison(node.word) === normalizedOriginal) {
      graphNode = node;
      graphNodeId = node.id;
      break;
    }
  }

  // Strategy 2: If text match failed, try position-based lookup but verify the word matches
  if (!graphNode && originalWordIndex < graphWords.length) {
    const candidate = graphWords[originalWordIndex];
    // Only use position-based result if the word text roughly matches
    if (normalizeForComparison(candidate.word) === normalizedOriginal) {
      graphNode = candidate;
      graphNodeId = candidate.id;
    }
  }

  return {
    originalWordIndex,
    originalWord,
    graphNodeId,
    graphNode,
  };
};

/**
 * Normalize a word for comparison
 * - Remove punctuation
 * - Lowercase
 */
const normalizeForComparison = (word: string): string => {
  return word
    .toLowerCase()
    .replace(/[.,!?;:'"()[{}«»""''—–\]-]+/g, '')
    .trim();
};

/**
 * Get all arcs connected to a specific node
 */
export const getConnectedArcs = (
  nodeId: string,
  dependencyGraph: DependencyGraphData
) => {
  return dependencyGraph.arcs.filter(
    arc => arc.from === nodeId || arc.to === nodeId
  );
};

/**
 * Get all nodes connected to a specific node (including the node itself)
 */
export const getConnectedNodeIds = (
  nodeId: string,
  dependencyGraph: DependencyGraphData
): Set<string> => {
  const connectedIds = new Set<string>([nodeId]);

  for (const arc of dependencyGraph.arcs) {
    if (arc.from === nodeId) {
      connectedIds.add(arc.to);
    }
    if (arc.to === nodeId) {
      connectedIds.add(arc.from);
    }
  }

  return connectedIds;
};
